import { FormEvent, useEffect, useRef, useState } from 'react'

import { BrowserProvider, JsonRpcProvider, JsonRpcSigner, parseUnits, toBigInt, Wallet } from 'ethers'
import { PluginClient } from '@remixproject/plugin'
import { ABIParameter, CompilationFileSources, CompilationResult, IRemixApi } from '@remixproject/plugin-api'
import { PluginApi, Status } from '@remixproject/plugin-utils'
import { useWeb3ModalProvider } from '@web3modal/ethers/react'

import Accordion from 'react-bootstrap/Accordion'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'

import CompiledContractsDropdown from '../shared/CompiledContractsDropdown'
import CompilerVersion from './CompilerVersion'
import { deployContract } from '../../utils/transactions'
import TransactionInput from '../shared/TransactionInput'
import TransactionValueForm from '../shared/TransactionValueForm'
import { useCompiledContractStore } from '../../zustand/compiledContractStore'
import { useCurrentFileStore } from '../../zustand/currentFileStore'
import { useDeployedContractStore } from '../../zustand/deployedContractStore'
import { useEnvironmentStore } from '../../zustand/environmentStore'
import SpinnerButton from '../shared/SpinnerButton'
import GasLimitForm from '../shared/TransactionGasLimitForm'

type DeployProps = {
    client: PluginClient<any, Readonly<IRemixApi>> & PluginApi<Readonly<IRemixApi>>
}

const Deploy = ({ client }: DeployProps) => {
    const { environment, publicKey, privateKey, rpcUrl } = useEnvironmentStore()
    const { walletProvider } = useWeb3ModalProvider()
    const { fileName, filePath, updateFileName, updateFilePath } = useCurrentFileStore()
    const [gasLimit, setGasLimit] = useState('0')
    const [value, setValue] = useState(toBigInt(0))
    const { compiledContracts, currentCompiledContractAbi, currentCompiledContractBytecode, currentCompiledContractName, reset, updateCompiledContracts } = useCompiledContractStore()
    const formRef = useRef<HTMLDivElement>(null)
    const inputRefs = useRef<(any | null)[]>([])
    const [showSpinner, setShowSpinner] = useState(false)
    const { addDeployedContract } = useDeployedContractStore()

    useEffect(() => {
        client.onload(async () => {
            client.on('fileManager', 'currentFileChanged', (filePath: string) => {
                reset()
    
                const name = filePath.replace(/^.*[\\/]/, '')
                const path = filePath.replace(name, '')
        
                updateFilePath(path)
                updateFileName(name)
            })

            client.solidity.on("statusChanged", (status: Status) => {
                setShowSpinner(status.key === 'loading')
            })
          
            client.solidity.on("compilationFinished", async (
                fileName: string,
                source: CompilationFileSources,
                languageVersion: string,
                data: CompilationResult
            ) => {
                updateCompiledContracts(data.contracts)
            })
        })
    }, [])

    const compileContract = async () => {
        const fullFilePath = `${filePath}${fileName}`

        await client.solidity.compile(fullFilePath)
    }

    const handleDeployContract = async () => {
        const constructorArgs: any[] = []

        if (currentCompiledContractAbi.length > 0 && currentCompiledContractAbi[0].type === 'constructor') {
            for (let i = 0; i < currentCompiledContractAbi[0].inputs!.length; i++) {
                constructorArgs.push(await inputRefs.current[i].handleUpdateValue())
            }
        }

        let signer: JsonRpcSigner | Wallet

        if (environment === 'Wallet') {
            if (walletProvider === undefined) throw Error('Wallet provider is undefined')
    
            const ethersProvider = new BrowserProvider(walletProvider)

            signer = await ethersProvider.getSigner()
        } else if (environment === 'Manual') {
            const provider = new JsonRpcProvider(rpcUrl)

            signer = new Wallet(privateKey, provider)
        } else {
            throw new Error('Unrecognized environment type')
        }

        const contractAddress = await deployContract(
            client,
            signer,
            currentCompiledContractName,
            currentCompiledContractAbi,
            currentCompiledContractBytecode,
            constructorArgs,
            value,
            gasLimit
        )

        if (contractAddress === '') {
            return
        }

        addDeployedContract({
            name: currentCompiledContractName,
            address: contractAddress.toString(),
            abi: currentCompiledContractAbi
        })
    }

    const loadContract = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const contractAddress = new FormData(event.currentTarget).get('load-from-address') as string

        addDeployedContract({
            name: currentCompiledContractName,
            address: contractAddress,
            abi: currentCompiledContractAbi
        })
    }

    return (
        <Accordion.Item eventKey='3'>
            <Accordion.Header><b>3 - Compile & Deploy</b></Accordion.Header>
            <Accordion.Body className='deploy-container'>
                <h6>Compile</h6>

                <CompilerVersion client={client} />

                <Button
                    className='compile-btn'
                    variant='primary'
                    size='sm'
                    onClick={async () => await compileContract()}
                    disabled={fileName === '' || fileName.substring(fileName.length - 4) !== '.sol'}
                >
                    {showSpinner
                        ? <Spinner size='sm' role='status' />
                        : <>Compile {fileName}</>
                    }
                </Button>

                <CompiledContractsDropdown />

                <h6 className='deploy-contract-header'>Deploy</h6>

                <GasLimitForm group='deploy' updateGasLimit={setGasLimit} />

                <TransactionValueForm updateValue={setValue} />

                <Form as='div' ref={formRef}>
                    {currentCompiledContractAbi.length > 0 && currentCompiledContractAbi[0].type === 'constructor' &&
                        <>
                            {currentCompiledContractAbi[0].inputs?.map((input, index) => {
                                return  <TransactionInput
                                            ref={(el: any) => (inputRefs.current[index] = el)}
                                            functionInputs={currentCompiledContractAbi[0].inputs! as ABIParameter[]}
                                            index={index}
                                            type={input.type}
                                            internalType={'internalType' in input ? input['internalType'] as string : input.type}
                                            name={input.name}
                                        />
                            })}
                        </>
                    }

                    <SpinnerButton
                        className='deploy-btn'
                        disabled={publicKey === '' || currentCompiledContractBytecode === ''}
                        handleFunction={handleDeployContract}
                        size='sm'
                        text='Deploy'
                        variant={
                            (currentCompiledContractAbi.length > 0 && 'stateMutability' in currentCompiledContractAbi[0] && currentCompiledContractAbi[0].stateMutability === 'payable')
                            ? 'danger'
                            : 'warning'
                        }
                    />
                </Form>

                <h6 className='load-contract-header'>Load From Address</h6>

                <Form onSubmit={loadContract}>
                    <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control type='text' name='load-from-address' required />
                    </Form.Group>

                    <Button variant='primary' size='sm' type='submit' className='load-contract-btn' disabled={compiledContracts.length === 0}>Load Contract</Button>
                </Form>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default Deploy
